import { useEffect } from 'react';
import * as Flux from 'dg-web-shared/lib/Flux';
import { useStore } from 'dg-web-shared/lib/Flux';
import { BasicButton } from 'dg-web-shared/tb-ui/buttons/BasicButton.tsx';
import { ButtonRow } from 'dg-web-shared/tb-ui/buttons/ButtonRow.tsx';
import * as Text from '../../../common/i18n/Text';
import { LeftColumn, RightColumn, StaticPage } from './StaticPage';
import { Helpdesk } from './Helpdesk';
import { Address } from './Address';
import { HasEverLoggedInState } from '../../home-unauthenticated/HasEverLoggedInState';
import { css } from '@emotion/css';
import { Colors } from 'dg-web-shared/ui/vars';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import {
    RequestStatus,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { DEVICE_UUID } from '../../../utils/ActionLog';
import { RELEASE_ID } from 'dg-web-shared/common/utils/release';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import * as GeneralTexts from '../../../common/i18n/GeneralTexts';

export interface Texts {
    Login: Text.Translation;
    AlreadyActive: Text.Translation;
    Success: Text.Translation;
    Failure: Text.Translation;
}

export function ActivateAccountPage() {
    const { update } = useStore(() => null);
    const navigate = useNavigate();
    const { token } = useParams();
    const [activate, sendActivate] = useServerWrite<
        { token?: string; appVersion: string; uuid: string },
        null,
        { code: number }
    >(() => ({
        url: '/ui-api/activate-account',
    }));

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        sendActivate({
            token,
            appVersion: RELEASE_ID,
            uuid: DEVICE_UUID,
        });
    }, [token]);
    /* eslint-enable react-hooks/exhaustive-deps */

    return (
        <StaticPage>
            <LeftColumn>
                <div
                    className={css({
                        maxWidth: '480px',
                        color: Colors.typo1,
                        ...Typo.body,
                    })}
                >
                    {activate.status === RequestStatus.SUCCESS ? (
                        <Localized
                            de={
                                'Das Konto wurde erfolgreich aktiviert. Bitte loggen Sie sich ein.'
                            }
                            fr={
                                'Le compte a été activé avec succès. Merci de vous connecter.'
                            }
                            it={
                                "Il conto è stato attivato con successo. Vogliate effettuare l'accesso."
                            }
                            en={
                                'The account has been activated successfully. Please log in.'
                            }
                        />
                    ) : activate.status === RequestStatus.ERROR ? (
                        activate.data?.code === -212 ? (
                            <Localized
                                de={'Dieses Konto wurde schon aktiviert.'}
                                fr={'Ce compte est déjà activé.'}
                                it={'Questo conto è già attivo'}
                                en={'This account has already been activated'}
                            />
                        ) : activate.data?.code === -105 ? (
                            <Localized
                                de="Der Aktivierungslink ist ungültig. Bitte überprüfen Sie, ob der Link in der E-Mail mit dem Link übereinstimmt, der im Browser geöffnet wurde. Sollte dies nicht der Fall sein, kopieren Sie ihn bitte manuell."
                                fr="Lien d'activation non valide. Veuillez vérifier que le lien figurant dans l'e-mail correspond bien à celui qui s'est ouvert dans le navigateur. Si ce n'est pas le cas, copiez-le manuellement."
                                it="Link d'attivazione non valido. Verificate che il link presente nel mail corrispondata a quello che è stato aperto nel browser. Se così non fosse, copiatelo manualmente."
                                en="Invalid activation link. Please check that the link in the email matches the one that has been opened in your browser. If this is not the case, please copy it manually."
                            />
                        ) : (
                            <Localized
                                de={GeneralTexts.texts.de.ErrorTryAgainOrHelpdesk()}
                                fr={GeneralTexts.texts.fr.ErrorTryAgainOrHelpdesk()}
                                it={GeneralTexts.texts.it.ErrorTryAgainOrHelpdesk()}
                                en={GeneralTexts.texts.en.ErrorTryAgainOrHelpdesk()}
                            />
                        )
                    ) : (
                        ''
                    )}
                    <ButtonRow>
                        <BasicButton
                            label={
                                <Localized
                                    de={'Einloggen'}
                                    fr={'Se connecter'}
                                    it={'Accedi'}
                                    en={'Sign in'}
                                />
                            }
                            negative={true}
                            disabled={
                                activate.status === RequestStatus.PENDING ||
                                activate.status === RequestStatus.NEVER_EXECUTED
                            }
                            onClick={() =>
                                update((store: Flux.Store) => {
                                    HasEverLoggedInState.stateWrite(store, {
                                        hasEver: true,
                                    });
                                    navigate('/');
                                    return 'gotoHomeFromConfirmedEmail';
                                })
                            }
                        />
                    </ButtonRow>
                </div>
            </LeftColumn>
            <RightColumn>
                <Helpdesk />
                <Address />
            </RightColumn>
        </StaticPage>
    );
}
